/**
 * RestrictedSignupPage
 *
 * Tenant-scoped public signup at /signup/:slug.
 *
 * Flow:
 *   1. On mount, GET /api/restricted-signup/:slug to validate the state
 *      exists AND is currently marked strict_isolated. If not → 404 card.
 *   2. Render a slim signup form: first_name, last_name, email, password,
 *      role (lead | affiliate only). NO state picker — it's implied by
 *      the URL and enforced server-side.
 *   3. POST /api/auth/register-restricted with the slug. Backend forces
 *      state + rejects anything other than lead/affiliate.
 *   4. On success, the user is logged in and sent to /.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Shield, Loader2 } from 'lucide-react';
import PortalMismatchDialog from '../components/PortalMismatchDialog';
import SignupCaptcha from '../components/SignupCaptcha';
import ApplicationPasswordModal from '../components/ApplicationPasswordModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_LIGHT = 'https://customer-assets.emergentagent.com/job_4f2b8787-9e39-421b-9343-dec62de2fe7e/artifacts/jrh9uqnh_Untitled%20design%20%287%29.png';

export default function RestrictedSignupPage() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { login } = useAuth();

  const [info, setInfo] = useState(null);     // {state, display_name, is_restricted}
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState('register'); // 'register' | 'login'
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: 'affiliate',
  });
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [mismatchRedirect, setMismatchRedirect] = useState(null);

  // ── CAPTCHA gate ──
  const [captchaOpen, setCaptchaOpen] = useState(false);
  const [formMountedAt, setFormMountedAt] = useState(0);
  useEffect(() => { setFormMountedAt(performance.now()); }, []);
  // iter 97 — Application Password gate (after captcha pass).
  const [appPasswordOpen, setAppPasswordOpen] = useState(false);
  const [pendingCaptcha, setPendingCaptcha] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      // Slug may arrive percent-encoded or already decoded depending
      // on the router/browser. Normalize by decoding first (swallow
      // malformed sequences), then letting axios encode once.
      let raw = slug || '';
      try { raw = decodeURIComponent(raw); } catch { /* malformed — use as-is */ }
      try {
        const r = await axios.get(`${API}/restricted-signup/${encodeURIComponent(raw)}`);
        if (alive) setInfo(r.data);
      } catch (e) {
        if (alive) setInfo(null);
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [slug]);

  const submit = (e) => {
    e.preventDefault();
    // Client-side password sanity — same rules the server enforces.
    const pw = form.password || '';
    const isStrong = pw.length >= 12
      && /[A-Z]/.test(pw)
      && /[0-9]/.test(pw)
      && /[^A-Za-z0-9]/.test(pw);
    if (!isStrong) {
      toast.error('Password must be at least 12 characters and include 1 uppercase letter, 1 number, and 1 special character.');
      return;
    }
    setCaptchaOpen(true);
  };

  const handleCaptchaPass = async ({ captcha_token, fingerprint }) => {
    setCaptchaOpen(false);
    setPendingCaptcha({ captcha_token, fingerprint });
    setAppPasswordOpen(true);
  };

  const handleAppPasswordSubmit = async (application_password) => {
    if (!pendingCaptcha) return;
    setSubmitting(true);
    try {
      let safeSlug = slug || '';
      try { safeSlug = decodeURIComponent(safeSlug); } catch { /* keep as-is */ }
      await axios.post(`${API}/auth/register-restricted`, {
        slug: safeSlug,
        email: form.email,
        password: form.password,
        first_name: form.first_name,
        last_name: form.last_name,
        role: form.role,
        captcha_token: pendingCaptcha.captcha_token,
        fingerprint: pendingCaptcha.fingerprint,
        application_password,
      });
      await login(form.email, form.password);
      setAppPasswordOpen(false);
      setPendingCaptcha(null);
      toast.success(`Welcome to ${info?.display_name}!`);
      navigate('/');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not create account';
      toast.error(detail);
      // Same UX rule as AuthPage — keep modal open ONLY for an
      // app-password mismatch. All other errors close the modal so
      // the user re-runs the captcha.
      if (!/application password/i.test(detail)) {
        setAppPasswordOpen(false);
        setPendingCaptcha(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleAppPasswordClose = async () => {
    // iter 98 — abandon counts as a signup attempt (anti-bot).
    setAppPasswordOpen(false);
    setPendingCaptcha(null);
    setSubmitting(false);
    try {
      await axios.post(`${API}/security/signup-abandon`, {
        email: form.email,
        fingerprint: pendingCaptcha?.fingerprint || '',
      });
    } catch (_e) { /* best-effort */ }
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Preflight scope check: raw axios login → check scope → only
      // hydrate AuthContext if the user belongs on THIS state's portal.
      let loginRes;
      try {
        loginRes = await axios.post(`${API}/auth/login`, {
          email: loginForm.email,
          password: loginForm.password,
        });
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Login failed');
        return;
      }
      const previewToken = loginRes.data?.access_token;
      if (previewToken) {
        try {
          const r = await axios.get(`${API}/auth/state-scope`, {
            headers: { Authorization: `Bearer ${previewToken}` },
          });
          const theirState = (r.data?.state || '').toUpperCase();
          const theirIsRestricted = !!r.data?.is_restricted;
          const thisSlug = (info?.state || '').toUpperCase();
          if (!theirIsRestricted) {
      setMismatchRedirect('/login');
            return;
          }
          if (theirState && theirState !== thisSlug) {
            setMismatchRedirect(`/signup/${encodeURIComponent(theirState)}`);
            return;
          }
        } catch { /* scope unknown — let login proceed */ }
      }
      await login(loginForm.email, loginForm.password);
      toast.success('Welcome back!');
      navigate('/');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3]"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))', paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))' }}
      >
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!info) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] p-4"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))', paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))' }}
      >
        <Card className="w-full max-w-md bg-white shadow-lg" data-testid="restricted-signup-invalid">
          <CardHeader className="text-center">
            <Shield className="w-12 h-12 mx-auto mb-2 text-muted-foreground opacity-40" />
            <CardTitle>Signup link not found</CardTitle>
            <CardDescription>
              Please contact your organization's administrator for further guidance.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] p-4"
      data-testid="restricted-signup-page"
      style={{ paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))', paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))' }}
    >
      <Card className="w-full max-w-md bg-white shadow-lg">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-16 h-16 rounded-2xl overflow-hidden flex items-center justify-center">
            <img src={LOGO_LIGHT} alt="The Uplift" className="w-full h-full object-contain" />
          </div>
          <CardTitle className="text-2xl font-bold" data-testid="restricted-signup-state-title">
            {info.display_name}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* mode tabs */}
          <div className="flex gap-2 mb-4">
            <button
              type="button"
              onClick={() => setMode('login')}
              className={`flex-1 py-2 rounded-full text-sm font-semibold border transition-colors ${
                mode === 'login' ? 'bg-[#586D52] text-white border-[#586D52]' : 'bg-white border-gray-200 hover:border-[#586D52]'
              }`}
              data-testid="restricted-mode-login"
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => setMode('register')}
              className={`flex-1 py-2 rounded-full text-sm font-semibold border transition-colors ${
                mode === 'register' ? 'bg-[#586D52] text-white border-[#586D52]' : 'bg-white border-gray-200 hover:border-[#586D52]'
              }`}
              data-testid="restricted-mode-register"
            >
              Sign Up
            </button>
          </div>

          {mode === 'register' ? (
            <form onSubmit={submit} className="space-y-3" data-testid="restricted-signup-form">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>First name</Label>
                  <Input
                    required
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    data-testid="restricted-first-name"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Last name</Label>
                  <Input
                    required
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    data-testid="restricted-last-name"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Email</Label>
                <Input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="restricted-email"
                />
              </div>
              <div className="space-y-1">
                <Label>Password</Label>
                <Input
                  type="password"
                  required
                  minLength={6}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="restricted-password"
                />
              </div>
              <div className="space-y-1">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="restricted-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="affiliate">Affiliate</SelectItem>
                    <SelectItem value="lead">Lead</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="submit"
                className="w-full rounded-full bg-[#586D52] hover:bg-[#4a5d46]"
                disabled={submitting}
                data-testid="restricted-signup-submit"
              >
                {submitting ? 'Creating…' : `Join ${info.display_name}`}
              </Button>
              <p
                className="text-[11px] text-center text-neutral-600 mt-2 leading-snug"
                data-testid="restricted-signup-legal-copy"
              >
                By creating an account, you agree to our{' '}
                <a
                  href="https://www.collectiveorb.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-[#586D52] hover:text-[#4a5d46] font-medium"
                  data-testid="restricted-signup-legal-privacy-link"
                >
                  Privacy Policy
                </a>
                {' '}&amp;{' '}
                <a
                  href="https://www.collectiveorb.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-[#586D52] hover:text-[#4a5d46] font-medium"
                  data-testid="restricted-signup-legal-tos-link"
                >
                  Terms of Service
                </a>
                .
              </p>
            </form>
          ) : (
            <form onSubmit={submitLogin} className="space-y-3" data-testid="restricted-login-form">
              <div className="space-y-1">
                <Label>Email</Label>
                <Input
                  type="email"
                  required
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  data-testid="restricted-login-email"
                />
              </div>
              <div className="space-y-1">
                <Label>Password</Label>
                <Input
                  type="password"
                  required
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  data-testid="restricted-login-password"
                />
              </div>
              <Button
                type="submit"
                className="w-full rounded-full bg-[#586D52] hover:bg-[#4a5d46]"
                disabled={submitting}
                data-testid="restricted-login-submit"
              >
                {submitting ? 'Signing in…' : 'Sign In'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
      <PortalMismatchDialog
        open={!!mismatchRedirect}
        redirectTo={mismatchRedirect}
        onRedirect={() => {
          const to = mismatchRedirect;
          setMismatchRedirect(null);
          if (to) window.location.assign(to);
        }}
      />
      <SignupCaptcha
        open={captchaOpen}
        onClose={() => { setCaptchaOpen(false); setSubmitting(false); }}
        onPass={handleCaptchaPass}
        email={form.email}
        formMountedAt={formMountedAt}
      />
      <ApplicationPasswordModal
        open={appPasswordOpen}
        onClose={handleAppPasswordClose}
        onSubmit={handleAppPasswordSubmit}
        submitting={submitting}
      />
    </div>
  );
}
